export const getDateFormat = (locale: string) => {
	if (locale === "en") return "en-GB";
	if (locale === "de") return "de-DE";
	if (locale === "es") return "es-ES";
	if (locale === "fr") return "fr-FR";
	if (locale === "it") return "it-IT";
	if (locale === "pt") return "pt-PT";
	if (locale === "az") return "az-AZ";
	if (locale === "lv") return "lv-LV";
	if (locale === "cs") return "cs-CZ";
	if (locale === "hr") return "hr-HR";
};

/** Chrome returns M01…M12 placeholders for Azerbaijani long/short months; map those to real names. */
const AZ_MONTHS_LONG = [
	"yanvar",
	"fevral",
	"mart",
	"aprel",
	"may",
	"iyun",
	"iyul",
	"avqust",
	"sentyabr",
	"oktyabr",
	"noyabr",
	"dekabr",
] as const;

const AZ_MONTHS_SHORT = [
	"yan",
	"fev",
	"mar",
	"apr",
	"may",
	"iyn",
	"iyl",
	"avq",
	"sen",
	"okt",
	"noy",
	"dek",
] as const;

const BROKEN_AZ_MONTH_PLACEHOLDER = /^M0*(\d{1,2})$/i;

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function resolveBrokenAzMonthName(
	placeholder: string,
	monthStyle: Intl.DateTimeFormatOptions["month"],
): string | null {
	const match = placeholder.match(BROKEN_AZ_MONTH_PLACEHOLDER);
	if (match === null) {
		return null;
	}

	const monthIndex = Number(match[1]) - 1;
	if (monthIndex < 0 || monthIndex > 11) {
		return null;
	}

	if (monthStyle === "long") {
		return AZ_MONTHS_LONG[monthIndex];
	}

	if (monthStyle === "short" || monthStyle === "narrow") {
		return AZ_MONTHS_SHORT[monthIndex];
	}

	return null;
}

/**
 * Formats a Date with Intl, replacing Chrome's broken Azerbaijani M01…M12 month placeholders.
 */
export function formatDateTimeForLocale(
	locale: string,
	date: Date,
	options: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" },
): string {
	const intlLocale = getDateFormat(locale) ?? locale;
	const formatter = new Intl.DateTimeFormat(intlLocale, options);

	return formatter
		.formatToParts(date)
		.map((part) => {
			if (part.type !== "month") {
				return part.value;
			}

			const azMonthName = resolveBrokenAzMonthName(part.value, options.month);
			return azMonthName ?? part.value;
		})
		.join("");
}

/**
 * Formats a date string for UI. Calendar-only values (`YYYY-MM-DD`, e.g. portal game list API)
 * are formatted in UTC so the calendar day matches the backend everywhere (avoids US vs Europe off-by-one).
 * Date-time strings fall back to the environment's local timezone.
 */
export function formatDateForLocaleDisplay(
	locale: string,
	dateInput: string | null | undefined,
	options: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" },
): string {
	if (dateInput === null || dateInput === undefined) {
		return "";
	}
	const trimmed = String(dateInput).trim();
	if (trimmed === "") {
		return "";
	}

	if (ISO_DATE_ONLY.test(trimmed)) {
		const parsed = new Date(`${trimmed}T00:00:00.000Z`);
		if (Number.isNaN(parsed.getTime())) {
			return "";
		}
		return formatDateTimeForLocale(locale, parsed, { ...options, timeZone: "UTC" });
	}

	const parsed = new Date(trimmed);
	if (Number.isNaN(parsed.getTime())) {
		return "";
	}
	return formatDateTimeForLocale(locale, parsed, options);
}
